import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useFilters
} from 'react-table';
import { Badge, Button, Card, Form, Modal } from 'react-bootstrap';
import {
  FaPaperclip,
  FaRoad,
  FaClock,
  FaCheckCircle,
  FaEdit,
  FaSave,
  FaTimes,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight
} from 'react-icons/fa';
import { normalizeModeCompteur } from '../utils/normalize';

/* ── helpers ── */
const toSafeValue = (v) => (v === null || v === undefined ? '' : v);

const getFieldLabel = (field) => ({
  compteur_prec: 'Compteur PREC',
  compteur_nv: 'Compteur NV',
  heures_reels: 'Heures réelles',
  gasoil: 'Gasoil (L)',
  reparation: 'Réparation',
  observation: 'Observation'
}[field] || 'Valeur');

const isNumericField = (field) =>
  ['compteur_prec', 'compteur_nv', 'heures_reels', 'gasoil'].includes(field);

const getFieldPlaceholder = (field) => ({
  compteur_prec: 'Saisir le compteur de départ',
  compteur_nv: "Saisir le compteur d'arrivée",
  heures_reels: "Saisir le nombre d'heures",
  gasoil: 'Saisir les litres de gasoil',
  reparation: 'Décrire la réparation',
  observation: 'Ajouter une observation'
}[field] || 'Saisir une valeur');

const getFieldTheme = (field) => ({
  compteur_prec: { bg: '#eef3ff', border: '#bfd0ff', color: '#3751d1' },
  compteur_nv:   { bg: '#eef3ff', border: '#bfd0ff', color: '#3751d1' },
  heures_reels:  { bg: '#eef7ff', border: '#bee5ff', color: '#0d6efd' },
  gasoil:        { bg: '#fff7ea', border: '#f3d7a5', color: '#d17b00' },
  reparation:    { bg: '#f8f2ea', border: '#d8c8b8', color: '#6d4e36' },
  observation:   { bg: '#f8f2ea', border: '#d8c8b8', color: '#6d4e36' }
}[field] || { bg: '#f8f9fa', border: '#dee2e6', color: '#495057' });

/* ── column filters ── */
const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => (
  <Form.Control
    size="sm"
    value={filterValue || ''}
    onChange={(e) => setFilter(e.target.value || undefined)}
    placeholder="Filtrer..."
    style={{ minWidth: 80, borderRadius: 8 }}
  />
);

const SelectColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id } }) => {
  const options = useMemo(() => {
    const s = new Set();
    preFilteredRows.forEach((r) => s.add(r.values[id]));
    return [...s].filter(Boolean).sort();
  }, [id, preFilteredRows]);

  return (
    <Form.Select
      size="sm"
      value={filterValue || ''}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{ minWidth: 100, borderRadius: 8 }}
    >
      <option value="">Tous</option>
      {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
    </Form.Select>
  );
};

/* ── main component ── */
const TableComponentMateriel = ({ data = [], onCellChange, onOpenDocuments }) => {
  const [editor, setEditor] = useState({
    show: false, rowId: null, field: '', value: '',
    matricule: '', type: '', mode_compteur: 'h'
  });
  const inputRef = useRef(null);

  useEffect(() => {
    if (editor.show && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        if (typeof inputRef.current?.select === 'function') inputRef.current.select();
      }, 80);
    }
  }, [editor.show]);

  const openEditor = (row, field) => {
    if (row?.dejaPresent) return;
    setEditor({
      show: true, rowId: row.id, field,
      value: toSafeValue(row?.[field]),
      matricule: row?.matricule || '',
      type: row?.type || '',
      mode_compteur: normalizeModeCompteur(row?.mode_compteur)
    });
  };

  const closeEditor = () =>
    setEditor({ show: false, rowId: null, field: '', value: '', matricule: '', type: '', mode_compteur: 'h' });

  const confirmEditor = () => {
    let finalValue = editor.value;
    if (isNumericField(editor.field)) {
      if (finalValue !== '') {
        const n = Number(finalValue);
        if (Number.isNaN(n) || n < 0) return;
        finalValue = String(finalValue);
      }
    }
    onCellChange(editor.rowId, editor.field, finalValue);
    closeEditor();
  };

  const EditableCell = ({ row, field, placeholder }) => {
    const isLocked = !!row.original.dejaPresent;
    const value = toSafeValue(row.original[field]);
    return (
      <Button
        variant="light"
        size="sm"
        disabled={isLocked}
        onClick={() => openEditor(row.original, field)}
        title={isLocked ? 'Ligne déjà pointée' : `Modifier ${getFieldLabel(field)}`}
        className="w-100 text-start d-flex align-items-center justify-content-between gap-1"
        style={{
          minHeight: 34,
          borderRadius: 8,
          border: '1px solid #d8c8b8',
          backgroundColor: isLocked ? '#eceff1' : '#fff',
          color: value ? '#212529' : '#adb5bd',
          fontSize: 12,
          padding: '4px 8px'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {value || placeholder}
        </span>
        {!isLocked && <FaEdit size={11} style={{ opacity: 0.5, flexShrink: 0 }} />}
      </Button>
    );
  };

  const columns = useMemo(() => [
    { Header: 'ID',        accessor: 'id',            Filter: DefaultColumnFilter, width: 65 },
    { Header: 'Matricule', accessor: 'matricule',      Filter: DefaultColumnFilter },
    { Header: 'Type',      accessor: 'type',           Filter: DefaultColumnFilter },
    { Header: 'Chantier',  accessor: 'chantier_actuel',Filter: SelectColumnFilter, Cell: ({ value }) => value || '—' },
    {
      Header: 'Mode', accessor: 'mode_compteur',
      disableFilters: true, width: 90, centerContent: true,
      Cell: ({ value }) => {
        const mode = normalizeModeCompteur(value);
        return mode === 'km'
          ? <Badge bg="primary" className="px-2 py-1"><FaRoad className="me-1" />KM</Badge>
          : <Badge bg="info" text="dark" className="px-2 py-1"><FaClock className="me-1" />Heure</Badge>;
      }
    },
    { Header: 'C. Prec',   accessor: 'compteur_prec',  disableFilters: true, width: 110, centerContent: true,
      Cell: ({ row }) => <EditableCell row={row} field="compteur_prec" placeholder="Départ" /> },
    { Header: 'C. Nv',     accessor: 'compteur_nv',    disableFilters: true, width: 110, centerContent: true,
      Cell: ({ row }) => <EditableCell row={row} field="compteur_nv" placeholder="Arrivée" /> },
    { Header: 'Heures',    accessor: 'heures_reels',   disableFilters: true, width: 100, centerContent: true,
      Cell: ({ row }) => <EditableCell row={row} field="heures_reels" placeholder="Heures" /> },
    { Header: 'Gasoil (L)',accessor: 'gasoil',         disableFilters: true, width: 100, centerContent: true,
      Cell: ({ row }) => <EditableCell row={row} field="gasoil" placeholder="Litres" /> },
    { Header: 'Réparation',accessor: 'reparation',     disableFilters: true, width: 150,
      Cell: ({ row }) => <EditableCell row={row} field="reparation" placeholder="Réparation" /> },
    { Header: 'Observation',accessor: 'observation',   disableFilters: true, width: 160,
      Cell: ({ row }) => <EditableCell row={row} field="observation" placeholder="Observation" /> },
    { Header: 'Docs', accessor: 'docs', disableSortBy: true, disableFilters: true, width: 55, centerContent: true,
      Cell: ({ row }) => (
        <Button size="sm" variant="outline-primary" style={{ borderRadius: 8 }}
          onClick={() => onOpenDocuments?.(row.original)}>
          <FaPaperclip />
        </Button>
      )
    },
    { Header: 'Statut', accessor: 'statut_affichage', disableSortBy: true, disableFilters: true, width: 120, centerContent: true,
      Cell: ({ row }) => row.original.dejaPresent
        ? <Badge bg="secondary" className="px-2 py-1"><FaCheckCircle className="me-1" />Déjà pointé</Badge>
        : <Badge bg="success" className="px-2 py-1">À saisir</Badge>
    }
  ], [onOpenDocuments, onCellChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    prepareRow, state, setGlobalFilter,
    pageOptions, gotoPage, nextPage, previousPage,
    canNextPage, canPreviousPage, setPageSize
  } = useTable(
    { columns, data, defaultColumn,
      autoResetFilters: false, autoResetSortBy: false,
      autoResetPage: false, autoResetGlobalFilter: false,
      initialState: { pageSize: 100 } },
    useFilters, useGlobalFilter, useSortBy, usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;
  const theme = getFieldTheme(editor.field);

  return (
    <>
      <Card className="mb-3" style={{ borderRadius: 16, border: '1px solid rgba(109,78,54,0.18)', boxShadow: '0 8px 24px rgba(109,78,54,0.08)' }}>
        <Card.Body>
          {/* toolbar */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div style={{ fontWeight: 700, color: '#6d4e36' }}>Liste des matériels</div>
            <div className="d-flex align-items-center gap-2">
              <Form.Select size="sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: 110, borderRadius: 10 }}>
                {[20, 50, 100, 200].map((s) => <option key={s} value={s}>{s} / page</option>)}
              </Form.Select>
            </div>
          </div>

          {/* global search */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <FaSearch />
            <Form.Control
              style={{ maxWidth: 320, borderRadius: 10 }}
              value={globalFilter || ''}
              onChange={(e) => setGlobalFilter(e.target.value || undefined)}
              placeholder="Recherche globale..."
            />
          </div>

          {/* table */}
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e8ddd4' }}>
            <table {...getTableProps()} className="table table-hover table-sm align-middle mb-0" style={{ fontSize: 13, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                {headerGroups.map((hg) => (
                  <React.Fragment key={hg.getHeaderGroupProps().key}>
                    {/* title row */}
                    <tr {...hg.getHeaderGroupProps()} className="table-dark">
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          key={col.id}
                          style={{
                            verticalAlign: 'middle', whiteSpace: 'nowrap',
                            width: col.width || undefined,
                            minWidth: col.width || 90,
                            textAlign: col.centerContent ? 'center' : 'left',
                            fontSize: 12, textTransform: 'uppercase',
                            letterSpacing: '0.04em', padding: '8px 10px'
                          }}
                        >
                          <div className={`d-flex align-items-center gap-1 ${col.centerContent ? 'justify-content-center' : 'justify-content-between'}`}>
                            <span>{col.render('Header')}</span>
                            {!col.disableSortBy && (
                              <span style={{ opacity: col.isSorted ? 1 : 0.4, fontSize: 10 }}>
                                {col.isSorted ? (col.isSortedDesc ? '▼' : '▲') : '⇅'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                    {/* filter row */}
                    <tr>
                      {hg.headers.map((col) => (
                        <th key={`${col.id}-f`} style={{ background: '#f5ede4', padding: '4px 6px', borderBottom: '1px solid #e0d0c4' }}>
                          {col.canFilter ? col.render('Filter') : null}
                        </th>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr><td colSpan={99} className="text-center text-muted py-4">Aucun matériel disponible</td></tr>
                ) : page.map((row, rowIndex) => {
                  prepareRow(row);
                  const isDeja = row.original.dejaPresent;
                  const rowBg = isDeja ? '#e8eaf0' : rowIndex % 2 === 0 ? '#fff' : '#faf7f4';
                  return (
                    <tr {...row.getRowProps()} key={row.getRowProps().key}
                      style={{ backgroundColor: rowBg, opacity: isDeja ? 0.78 : 1 }}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} key={cell.getCellProps().key}
                          style={{
                            verticalAlign: 'middle',
                            textAlign: cell.column.centerContent ? 'center' : 'left',
                            padding: '4px 8px',
                            borderBottom: '1px solid #ede5dc',
                            borderRight: '1px solid #f0e8e0'
                          }}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
            <div className="text-muted small">
              Page <strong>{pageIndex + 1}</strong> / <strong>{pageOptions.length || 1}</strong>
              <span className="ms-2" style={{ color: '#aaa' }}>({data.length} ligne{data.length > 1 ? 's' : ''})</span>
            </div>
            <div className="d-flex gap-1">
              <Button variant="outline-secondary" size="sm" onClick={() => gotoPage(0)} disabled={!canPreviousPage} style={{ borderRadius: 8 }}><FaAngleDoubleLeft /></Button>
              <Button variant="outline-secondary" size="sm" onClick={() => previousPage()} disabled={!canPreviousPage} style={{ borderRadius: 8 }}><FaChevronLeft /></Button>
              <Button variant="outline-secondary" size="sm" onClick={() => nextPage()} disabled={!canNextPage} style={{ borderRadius: 8 }}><FaChevronRight /></Button>
              <Button variant="outline-secondary" size="sm" onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} style={{ borderRadius: 8 }}><FaAngleDoubleRight /></Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* editor modal */}
      <Modal show={editor.show} onHide={closeEditor} centered backdrop="static" keyboard>
        <Modal.Header closeButton style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
          <Modal.Title style={{ color: theme.color, fontWeight: 800 }}>
            ✏️ Modifier {getFieldLabel(editor.field)}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: '#fffdf8' }}>
          <div className="mb-3" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 12 }}>
            <div className="small text-muted">Matériel</div>
            <div style={{ fontWeight: 700 }}>{editor.matricule || '—'}</div>
            <div className="small text-muted">{editor.type || '—'}</div>
            <div className="mt-2">
              {editor.mode_compteur === 'km'
                ? <Badge bg="primary"><FaRoad className="me-1" />Mode KM</Badge>
                : <Badge bg="info" text="dark"><FaClock className="me-1" />Mode Heure</Badge>}
            </div>
          </div>

          <Form.Group>
            <Form.Label style={{ fontWeight: 700, color: '#6d4e36' }}>{getFieldLabel(editor.field)}</Form.Label>
            {isNumericField(editor.field) ? (
              <Form.Control
                ref={inputRef} type="number" min="0" step="0.01"
                value={editor.value}
                onChange={(e) => setEditor((p) => ({ ...p, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmEditor(); } }}
                placeholder={getFieldPlaceholder(editor.field)}
                style={{ minHeight: 48, borderRadius: 12, borderColor: theme.border, fontSize: 18, fontWeight: 600 }}
              />
            ) : (
              <Form.Control
                ref={inputRef} as="textarea" rows={4}
                value={editor.value}
                onChange={(e) => setEditor((p) => ({ ...p, value: e.target.value }))}
                onKeyDown={(e) => { if (e.ctrlKey && e.key === 'Enter') { e.preventDefault(); confirmEditor(); } }}
                placeholder={getFieldPlaceholder(editor.field)}
                style={{ borderRadius: 12, borderColor: theme.border, fontSize: 15 }}
              />
            )}
            <div className="small text-muted mt-2">
              {isNumericField(editor.field)
                ? 'Saisis la valeur complète puis clique sur Valider.'
                : 'Utilise Ctrl + Entrée pour valider rapidement.'}
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer style={{ background: '#fffdf8', borderTop: '1px solid #eee' }}>
          <Button variant="outline-secondary" onClick={closeEditor} style={{ borderRadius: 10 }}>
            <FaTimes className="me-2" />Annuler
          </Button>
          <Button onClick={confirmEditor} style={{ borderRadius: 10, backgroundColor: theme.color, borderColor: theme.color }}>
            <FaSave className="me-2" />Valider
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TableComponentMateriel;
